import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useCurrentUser } from '../hooks/useCurrentUser';
import missionService, { MissionCase, MissionStats } from '../services/missionService';
import {
  Heart,
  MapPin,
  BookOpen,
  Send,
  Plus,
  X,
  CheckCircle,
  Users,
  Target,
  HeartHandshake,
  Loader2,
  ClipboardList,
  Eye,
  HandHeart,
  Cross,
  FileText,
} from 'lucide-react';

const urgencyMap: Record<string, { label: string; color: string }> = {
  alta_urgencia: { label: 'Alta urgencia', color: 'bg-rose-50 text-rose-700 ring-rose-200' },
  urgente: { label: 'Urgente', color: 'bg-amber-50 text-amber-700 ring-amber-200' },
  acompanhamento: { label: 'Acompanhamento', color: 'bg-blue-50 text-blue-700 ring-blue-200' },
};

const missionSteps = [
  'Vou agir com respeito',
  'Nao vou julgar',
  'Vou levar presenca',
  'Vou acolher com amor',
];

const missionKit = [
  { title: 'Agua benta', description: 'Sinal de esperanca e fe', icon: '💧' },
  { title: 'Terco', description: 'Apoio para oracao simples', icon: '📿' },
  { title: 'Mensagem', description: 'Palavra breve que consola', icon: '💌' },
  { title: 'Presenca', description: 'O maior presente da visita', icon: '🤝' },
];

const Missoes: React.FC = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  useCurrentUser();
  const [cases, setCases] = useState<MissionCase[]>([]);
  const [stats, setStats] = useState<MissionStats>({ missions_completed: 0, people_helped: 0, active_volunteers: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'cases' | 'mine' | 'reports' | 'refer' | 'kit'>('cases');
  const [myReports, setMyReports] = useState<any[]>([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [myParticipations, setMyParticipations] = useState<any[]>([]);
  const [isLoadingMine, setIsLoadingMine] = useState(false);

  // Report modal state
  const [reportParticipation, setReportParticipation] = useState<any | null>(null);
  const [reportForm, setReportForm] = useState({ report: '', needs_continuity: '', new_needs_identified: '' });
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  // Refer form state
  const [referForm, setReferForm] = useState({
    name: '', city: '', situation_type: '', description: '', contact: '',
  });
  const [isSubmittingRefer, setIsSubmittingRefer] = useState(false);
  const [referSuccess, setReferSuccess] = useState(false);

  // Participation modal state
  const [selectedCase, setSelectedCase] = useState<MissionCase | null>(null);
  const [participationType, setParticipationType] = useState<string>('');
  const [commitments, setCommitments] = useState<boolean[]>([false, false, false, false]);
  const [isSubmittingParticipation, setIsSubmittingParticipation] = useState(false);


  useEffect(() => {
    loadData();
    loadMyParticipations();
    loadMyReports();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [casesData, statsData] = await Promise.all([
        missionService.getCases(),
        missionService.getStats(),
      ]);
      setCases(casesData || []);
      setStats(statsData || { missions_completed: 0, people_helped: 0, active_volunteers: 0 });
    } catch (error) {
      console.error('Error loading mission data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMyParticipations = async () => {
    setIsLoadingMine(true);
    try {
      const data = await missionService.getMyParticipations();
      setMyParticipations(data || []);
    } catch (error) {
      console.error('Error loading my participations:', error);
    } finally {
      setIsLoadingMine(false);
    }
  };

  const loadMyReports = async () => {
    setIsLoadingReports(true);
    try {
      const data = await missionService.getMyReports();
      setMyReports(data || []);
    } catch (error) {
      console.error('Error loading my reports:', error);
    } finally {
      setIsLoadingReports(false);
    }
  };

  const handleReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reportParticipation || !reportForm.report.trim()) return;
    setIsSubmittingReport(true);
    try {
      await missionService.createReport({
        participation_id: reportParticipation.id,
        report: reportForm.report,
        needs_continuity: reportForm.needs_continuity === 'sim',
        new_needs_identified: reportForm.new_needs_identified || null,
      });
      setReportSuccess(true);
      setReportForm({ report: '', needs_continuity: '', new_needs_identified: '' });
      setTimeout(() => {
        setReportParticipation(null);
        setReportSuccess(false);
      }, 2000);
      loadMyParticipations();
      loadMyReports();
    } catch (error) {
      console.error('Error creating report:', error);
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const participationTypeLabels: Record<string, { label: string; icon: typeof Eye }> = {
    visit: { label: 'Visita', icon: Eye },
    help: { label: 'Ajuda', icon: HandHeart },
    pray: { label: 'Oracao', icon: Cross },
  };

  const statusLabels: Record<string, { label: string; color: string }> = {
    pending: { label: 'Pendente', color: 'bg-amber-50 text-amber-700 ring-amber-200' },
    confirmed: { label: 'Confirmada', color: 'bg-blue-50 text-blue-700 ring-blue-200' },
    completed: { label: 'Concluida', color: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
    cancelled: { label: 'Cancelada', color: 'bg-stone-100 text-stone-500 ring-stone-200' },
  };

  const handleRefer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!referForm.name.trim()) return;
    setIsSubmittingRefer(true);
    try {
      await missionService.createCase({
        name: referForm.name,
        city: referForm.city || null,
        situation_type: referForm.situation_type || null,
        description: referForm.description || null,
        contact: referForm.contact || null,
        urgency: 'acompanhamento',
        needs: [],
      });
      setReferSuccess(true);
      setReferForm({ name: '', city: '', situation_type: '', description: '', contact: '' });
      loadData();
      setTimeout(() => setReferSuccess(false), 4000);
    } catch (error) {
      console.error('Error creating referral:', error);
    } finally {
      setIsSubmittingRefer(false);
    }
  };

  const handleParticipation = async () => {
    if (!selectedCase || !participationType) return;
    const allChecked = commitments.every(Boolean);
    if (participationType === 'visit' && !allChecked) return;

    setIsSubmittingParticipation(true);
    try {
      await missionService.createParticipation({
        case_id: selectedCase.id!,
        participation_type: participationType,
        commitments: missionSteps.filter((_, i) => commitments[i]),
      });
      setSelectedCase(null);
      setParticipationType('');
      setCommitments([false, false, false, false]);
      loadData();
      loadMyParticipations();
      setActiveTab('mine');
    } catch (error) {
      console.error('Error creating participation:', error);
    } finally {
      setIsSubmittingParticipation(false);
    }
  };

  const openParticipationModal = (missionCase: MissionCase, type: string) => {
    setSelectedCase(missionCase);
    setParticipationType(type);
    setCommitments([false, false, false, false]);
  };

  const statCards = [
    { label: 'Missoes realizadas', value: stats.missions_completed, icon: Target },
    { label: 'Pessoas ajudadas', value: stats.people_helped, icon: HeartHandshake },
    { label: 'Voluntarios ativos', value: stats.active_volunteers, icon: Users },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <Sidebar currentPath="/missoes" onToggle={setSidebarExpanded} />

      <div className={`transition-all duration-300 ml-0 pt-14 md:pt-0 ${sidebarExpanded ? 'md:ml-64' : 'md:ml-16'}`}>
        {/* Header */}
        <header className="sticky top-0 z-40 border-b border-stone-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-700">
                SantoGram
              </div>
              <h1 className="text-xl font-semibold tracking-tight text-stone-900 sm:text-2xl">
                Missao Caminho dos Santos
              </h1>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 px-3 md:px-6 pb-0 overflow-x-auto">
            {[
              { key: 'cases', label: 'Casos ativos', icon: Heart },
              { key: 'mine', label: 'Minhas missoes', icon: ClipboardList },
              { key: 'reports', label: 'Acompanhamentos', icon: FileText },
              { key: 'refer', label: 'Indicar alguem', icon: Send },
              { key: 'kit', label: 'Manual das missões', icon: BookOpen },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center gap-2 rounded-t-xl px-3 md:px-4 py-2.5 text-sm font-medium transition whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-stone-50 text-amber-700 border-b-2 border-amber-600'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </header>

        <main className="p-6">
          {/* Tab: Casos ativos */}
          {activeTab === 'cases' && (
            <div>
              <div className="mb-6 flex items-end justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-stone-900">Pessoas que precisam de ajuda</h2>
                  <p className="mt-1 text-sm text-stone-500">
                    Casos reais com necessidades claras. Voce pode visitar, ajudar ou rezar.
                  </p>
                </div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={32} className="animate-spin text-amber-600" />
                </div>
              ) : cases.length === 0 ? (
                <div className="rounded-2xl bg-white p-12 text-center shadow-sm ring-1 ring-stone-200">
                  <Heart size={48} className="mx-auto text-stone-300" />
                  <h3 className="mt-4 text-lg font-semibold text-stone-700">Nenhum caso ativo</h3>
                  <p className="mt-2 text-sm text-stone-500">
                    Ainda nao ha casos cadastrados. Voce pode indicar alguem que precisa de ajuda.
                  </p>
                  <button
                    onClick={() => setActiveTab('refer')}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-95"
                  >
                    <Plus size={16} />
                    Indicar alguem
                  </button>
                </div>
              ) : (
                <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
                  {cases.map((item) => {
                    const urgency = urgencyMap[item.urgency || 'acompanhamento'] || urgencyMap.acompanhamento;
                    return (
                      <article key={item.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-stone-200 transition hover:-translate-y-0.5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h4 className="text-lg font-semibold text-stone-900">{item.name}</h4>
                            {(item.city || item.state) && (
                              <p className="mt-1 flex items-center gap-1 text-sm text-stone-500">
                                <MapPin size={14} />
                                {[item.city, item.state].filter(Boolean).join(' - ')}
                              </p>
                            )}
                          </div>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${urgency.color}`}>
                            {urgency.label}
                          </span>
                        </div>

                        {item.summary && (
                          <p className="mt-4 text-sm leading-relaxed text-stone-600">{item.summary}</p>
                        )}

                        {item.needs && item.needs.length > 0 && (
                          <div className="mt-4">
                            <div className="text-sm font-medium text-stone-700">Necessidades</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {item.needs.map((need) => (
                                <span key={need} className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600">
                                  {need}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {item.patron_saint && (
                          <div className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-100">
                            📿 Santo que acompanha: <strong>{item.patron_saint}</strong>
                          </div>
                        )}

                        <div className="mt-5 grid gap-2">
                          <button
                            onClick={() => openParticipationModal(item, 'visit')}
                            className="rounded-xl bg-stone-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95"
                          >
                            Quero visitar
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => openParticipationModal(item, 'help')}
                              className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95"
                            >
                              Ajudar
                            </button>
                            <button
                              onClick={() => openParticipationModal(item, 'pray')}
                              className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 ring-1 ring-stone-200 transition hover:bg-stone-50"
                            >
                              Rezar
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tab: Minhas missoes */}
          {activeTab === 'mine' && (
            <div>
              <div className="mb-6 flex items-end justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-stone-900">Minhas missoes</h2>
                  <p className="mt-1 text-sm text-stone-500">
                    Acompanhe as missoes em que voce se inscreveu e registre seus relatorios.
                  </p>
                </div>
              </div>

              {/* Stats do voluntario */}
              <div className="mb-6 grid gap-4 sm:grid-cols-3">
                {statCards.map((stat) => (
                  <div key={stat.label} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-stone-200">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-amber-50 p-2.5">
                        <stat.icon size={20} className="text-amber-700" />
                      </div>
                      <div>
                        <div className="text-2xl font-semibold text-stone-900">{stat.value}</div>
                        <div className="text-sm text-stone-500">{stat.label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {isLoadingMine ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={32} className="animate-spin text-amber-600" />
                </div>
              ) : myParticipations.length === 0 ? (
                <div className="rounded-2xl bg-white p-12 text-center shadow-sm ring-1 ring-stone-200">
                  <ClipboardList size={48} className="mx-auto text-stone-300" />
                  <h3 className="mt-4 text-lg font-semibold text-stone-700">Nenhuma missao ainda</h3>
                  <p className="mt-2 text-sm text-stone-500">
                    Voce ainda nao participou de nenhuma missao. Veja os casos ativos e participe!
                  </p>
                  <button
                    onClick={() => setActiveTab('cases')}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-95"
                  >
                    <Heart size={16} />
                    Ver casos ativos
                  </button>
                </div>
              ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                  {myParticipations.map((participation) => {
                    const missionCase = participation.mission_cases;
                    const typeInfo = participationTypeLabels[participation.participation_type] || { label: participation.participation_type, icon: Heart };
                    const statusInfo = statusLabels[participation.status] || statusLabels.pending;
                    const TypeIcon = typeInfo.icon;

                    return (
                      <div key={participation.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-stone-200">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="rounded-xl bg-amber-50 p-2.5">
                              <TypeIcon size={20} className="text-amber-700" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-stone-900">{missionCase?.name || 'Caso'}</h4>
                              <div className="mt-0.5 flex items-center gap-2 text-sm text-stone-500">
                                <span>{typeInfo.label}</span>
                                {missionCase?.city && (
                                  <>
                                    <span>-</span>
                                    <span className="flex items-center gap-1">
                                      <MapPin size={12} />
                                      {missionCase.city}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>

                        {missionCase?.summary && (
                          <p className="mt-3 text-sm leading-relaxed text-stone-600">{missionCase.summary}</p>
                        )}

                        {participation.commitments && participation.commitments.length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs font-medium uppercase tracking-wider text-stone-400">Compromissos</div>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {participation.commitments.map((c: string, i: number) => (
                                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                                  <CheckCircle size={12} />
                                  {c}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-4 flex items-center justify-between border-t border-stone-100 pt-3">
                          <span className="text-xs text-stone-400">
                            {new Date(participation.created_at).toLocaleDateString('pt-BR')}
                          </span>
                          {participation.participation_type === 'visit' && participation.status !== 'cancelled' && (
                            <button
                              onClick={() => {
                                setReportParticipation(participation);
                                setReportForm({ report: '', needs_continuity: '', new_needs_identified: '' });
                                setReportSuccess(false);
                              }}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-95"
                            >
                              <FileText size={14} />
                              Registrar acompanhamento
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tab: Acompanhamentos */}
          {activeTab === 'reports' && (
            <div>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-stone-900">Meus acompanhamentos</h2>
                <p className="mt-1 text-sm text-stone-500">
                  Historico de todos os relatorios que voce registrou apos suas visitas.
                </p>
              </div>

              {isLoadingReports ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={32} className="animate-spin text-amber-600" />
                </div>
              ) : myReports.length === 0 ? (
                <div className="rounded-2xl bg-white p-12 text-center shadow-sm ring-1 ring-stone-200">
                  <FileText size={48} className="mx-auto text-stone-300" />
                  <h3 className="mt-4 text-lg font-semibold text-stone-700">Nenhum acompanhamento</h3>
                  <p className="mt-2 text-sm text-stone-500">
                    Voce ainda nao registrou nenhum acompanhamento. Apos realizar uma visita, registre como foi.
                  </p>
                  <button
                    onClick={() => setActiveTab('mine')}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-95"
                  >
                    <ClipboardList size={16} />
                    Ver minhas missoes
                  </button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {myReports.map((report) => {
                    const participation = report.mission_participations;
                    const missionCase = participation?.mission_cases;

                    return (
                      <div key={report.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-stone-200">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h4 className="font-semibold text-stone-900">
                              {missionCase?.name || 'Caso'}
                            </h4>
                            <div className="mt-0.5 flex items-center gap-2 text-sm text-stone-500">
                              {missionCase?.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={12} />
                                  {missionCase.city}
                                </span>
                              )}
                              <span>-</span>
                              <span>{new Date(report.created_at).toLocaleDateString('pt-BR')}</span>
                            </div>
                          </div>
                          {report.needs_continuity && (
                            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                              Precisa continuidade
                            </span>
                          )}
                        </div>

                        <div className="mt-4 rounded-xl bg-stone-50 p-4 text-sm leading-relaxed text-stone-700 ring-1 ring-stone-100">
                          {report.report}
                        </div>

                        {report.new_needs_identified && (
                          <div className="mt-3 flex items-start gap-2 text-sm">
                            <span className="font-medium text-stone-600">Nova necessidade:</span>
                            <span className="text-stone-500">{report.new_needs_identified}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tab: Indicar alguem */}
          {activeTab === 'refer' && (
            <div className="mx-auto max-w-3xl">
              <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
                <div className="rounded-2xl bg-amber-50 p-6 ring-1 ring-amber-100">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Indicacao</div>
                  <h3 className="mt-2 text-xl font-semibold tracking-tight text-stone-900">
                    Indique alguem com respeito e cuidado
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-700">
                    O objetivo nao e expor a dor de ninguem, mas abrir caminho para acolhimento. O envio pode ser simples, com o minimo necessario para avaliar o caso.
                  </p>
                </div>

                <form onSubmit={handleRefer} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-stone-200">
                  {referSuccess && (
                    <div className="mb-5 flex items-center gap-2 rounded-xl bg-emerald-50 p-4 text-sm font-medium text-emerald-800 ring-1 ring-emerald-200">
                      <CheckCircle size={18} />
                      Indicacao enviada com sucesso!
                    </div>
                  )}

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-stone-700">Nome *</label>
                      <input
                        type="text"
                        placeholder="Nome ou apelido"
                        value={referForm.name}
                        onChange={(e) => setReferForm({ ...referForm, name: e.target.value })}
                        className="w-full rounded-xl border-0 bg-stone-50 px-4 py-2.5 text-sm text-stone-800 ring-1 ring-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-600"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-stone-700">Cidade</label>
                      <input
                        type="text"
                        placeholder="Cidade e estado"
                        value={referForm.city}
                        onChange={(e) => setReferForm({ ...referForm, city: e.target.value })}
                        className="w-full rounded-xl border-0 bg-stone-50 px-4 py-2.5 text-sm text-stone-800 ring-1 ring-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-600"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-stone-700">Situacao</label>
                      <div className="grid gap-2 sm:grid-cols-3">
                        {[
                          { value: 'saude', label: 'Saude' },
                          { value: 'financeiro', label: 'Financeiro' },
                          { value: 'emocional', label: 'Emocional' },
                        ].map((type) => (
                          <label
                            key={type.value}
                            className={`flex cursor-pointer items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium ring-1 transition ${
                              referForm.situation_type === type.value
                                ? 'bg-amber-50 text-amber-800 ring-amber-300'
                                : 'bg-stone-50 text-stone-600 ring-stone-200 hover:bg-stone-100'
                            }`}
                          >
                            <input
                              type="radio"
                              name="situation"
                              value={type.value}
                              checked={referForm.situation_type === type.value}
                              onChange={(e) => setReferForm({ ...referForm, situation_type: e.target.value })}
                              className="sr-only"
                            />
                            {type.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-stone-700">Descricao</label>
                      <textarea
                        placeholder="Explique brevemente a necessidade"
                        value={referForm.description}
                        onChange={(e) => setReferForm({ ...referForm, description: e.target.value })}
                        className="min-h-[120px] w-full rounded-xl border-0 bg-stone-50 px-4 py-2.5 text-sm text-stone-800 ring-1 ring-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-600"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-stone-700">Contato (opcional)</label>
                      <input
                        type="text"
                        placeholder="Telefone ou WhatsApp"
                        value={referForm.contact}
                        onChange={(e) => setReferForm({ ...referForm, contact: e.target.value })}
                        className="w-full rounded-xl border-0 bg-stone-50 px-4 py-2.5 text-sm text-stone-800 ring-1 ring-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-600"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmittingRefer}
                    className="mt-5 w-full rounded-xl bg-amber-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
                  >
                    {isSubmittingRefer ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 size={16} className="animate-spin" /> Enviando...
                      </span>
                    ) : (
                      'Enviar com amor'
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Tab: Kit da missao */}
          {activeTab === 'kit' && (
            <div>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-stone-900">O que levar em uma visita</h2>
                <p className="mt-1 text-sm text-stone-500">
                  Um espaco educativo para orientar o voluntario. O foco nao e complexidade, mas presenca significativa.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {missionKit.map((item) => (
                  <div key={item.title} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-stone-200">
                    <div className="text-3xl">{item.icon}</div>
                    <div className="mt-3 text-lg font-semibold text-stone-900">{item.title}</div>
                    <p className="mt-2 text-sm leading-relaxed text-stone-500">{item.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-2xl bg-amber-50 p-5 text-sm leading-relaxed text-amber-900 ring-1 ring-amber-100">
                Lembre-se: a maior ajuda nem sempre e material. Muitas vezes, o que cura primeiro e a atencao, a escuta e a presenca.
              </div>

              {/* Mission flow section */}
              <div className="mt-8">
                <h2 className="text-lg font-semibold text-stone-900">Fluxo da missao</h2>
                <p className="mt-1 mb-6 text-sm text-stone-500">
                  Antes de confirmar uma visita, o voluntario assume um compromisso de respeito, acolhimento e presenca.
                </p>

                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl bg-stone-900 p-6 text-white shadow-sm">
                    <h4 className="text-lg font-semibold">Voce esta entrando em uma missao real</h4>
                    <p className="mt-2 text-sm leading-relaxed text-stone-300">
                      Ao confirmar uma visita, voce aceita os compromissos abaixo.
                    </p>
                    <div className="mt-5 grid gap-3">
                      {missionSteps.map((step, index) => (
                        <div key={step} className="flex items-center gap-3 rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
                          <CheckCircle size={18} className="text-amber-400" />
                          <span className="text-sm font-medium text-stone-100">
                            {index + 1}. {step}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-stone-100 p-6 ring-1 ring-stone-200">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Pos-visita</div>
                    <h4 className="mt-2 text-lg font-semibold text-stone-900">Registrar acompanhamento</h4>
                    <p className="mt-2 text-sm leading-relaxed text-stone-600">
                      Apos a visita, voce pode registrar o relato, indicar continuidade e apontar novas necessidades. Isso ajuda o SantoGram a organizar o historico de cuidado.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Participation Modal */}
      {selectedCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-stone-900">
                  {participationType === 'visit' && 'Confirmar visita'}
                  {participationType === 'help' && 'Confirmar ajuda'}
                  {participationType === 'pray' && 'Comprometer-se em oracao'}
                </h3>
                <p className="mt-1 text-sm text-stone-500">Caso: {selectedCase.name}</p>
              </div>
              <button
                onClick={() => setSelectedCase(null)}
                className="rounded-lg p-1 hover:bg-stone-100"
              >
                <X size={20} className="text-stone-500" />
              </button>
            </div>

            {participationType === 'visit' && (
              <div className="mt-5">
                <p className="mb-4 text-sm text-stone-600">
                  Antes de confirmar, aceite os compromissos:
                </p>
                <div className="grid gap-3">
                  {missionSteps.map((step, index) => (
                    <label
                      key={step}
                      className="flex cursor-pointer items-center gap-3 rounded-xl bg-stone-50 p-3 ring-1 ring-stone-200"
                    >
                      <input
                        type="checkbox"
                        checked={commitments[index]}
                        onChange={() => {
                          const newCommitments = [...commitments];
                          newCommitments[index] = !newCommitments[index];
                          setCommitments(newCommitments);
                        }}
                        className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm font-medium text-stone-700">
                        {index + 1}. {step}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {participationType === 'help' && (
              <div className="mt-5">
                <p className="text-sm text-stone-600">
                  Voce esta se comprometendo a ajudar <strong>{selectedCase.name}</strong>.
                  {selectedCase.needs && selectedCase.needs.length > 0 && (
                    <span> Necessidades identificadas: {selectedCase.needs.join(', ')}.</span>
                  )}
                </p>
              </div>
            )}

            {participationType === 'pray' && (
              <div className="mt-5">
                <p className="text-sm text-stone-600">
                  Voce esta se comprometendo a rezar por <strong>{selectedCase.name}</strong>.
                  {selectedCase.patron_saint && (
                    <span> Santo que acompanha esta missao: <strong>{selectedCase.patron_saint}</strong>.</span>
                  )}
                </p>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setSelectedCase(null)}
                className="flex-1 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 ring-1 ring-stone-200 transition hover:bg-stone-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleParticipation}
                disabled={
                  isSubmittingParticipation ||
                  (participationType === 'visit' && !commitments.every(Boolean))
                }
                className="flex-1 rounded-xl bg-amber-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
              >
                {isSubmittingParticipation ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Confirmando...
                  </span>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Report Modal */}
      {reportParticipation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Registrar acompanhamento</h3>
                <p className="mt-1 text-sm text-stone-500">
                  Caso: {reportParticipation.mission_cases?.name || 'Missao'}
                </p>
              </div>
              <button
                onClick={() => setReportParticipation(null)}
                className="rounded-lg p-1 hover:bg-stone-100"
              >
                <X size={20} className="text-stone-500" />
              </button>
            </div>

            {reportSuccess ? (
              <div className="mt-5 flex items-center gap-2 rounded-xl bg-emerald-50 p-4 text-sm font-medium text-emerald-800 ring-1 ring-emerald-200">
                <CheckCircle size={18} />
                Relatorio enviado com sucesso!
              </div>
            ) : (
              <form onSubmit={handleReport} className="mt-5 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-stone-700">Como foi a missao? *</label>
                  <textarea
                    placeholder="Conte brevemente como foi a visita..."
                    value={reportForm.report}
                    onChange={(e) => setReportForm({ ...reportForm, report: e.target.value })}
                    className="min-h-[120px] w-full rounded-xl border-0 bg-stone-50 px-4 py-2.5 text-sm text-stone-800 ring-1 ring-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-600"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-stone-700">Precisa de continuidade?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['sim', 'nao'].map((opt) => (
                      <label
                        key={opt}
                        className={`flex cursor-pointer items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium ring-1 transition ${
                          reportForm.needs_continuity === opt
                            ? 'bg-amber-50 text-amber-800 ring-amber-300'
                            : 'bg-stone-50 text-stone-600 ring-stone-200 hover:bg-stone-100'
                        }`}
                      >
                        <input
                          type="radio"
                          name="continuity"
                          value={opt}
                          checked={reportForm.needs_continuity === opt}
                          onChange={(e) => setReportForm({ ...reportForm, needs_continuity: e.target.value })}
                          className="sr-only"
                        />
                        {opt === 'sim' ? 'Sim' : 'Nao'}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-stone-700">Nova necessidade identificada</label>
                  <input
                    type="text"
                    placeholder="Descreva se identificou algo novo"
                    value={reportForm.new_needs_identified}
                    onChange={(e) => setReportForm({ ...reportForm, new_needs_identified: e.target.value })}
                    className="w-full rounded-xl border-0 bg-stone-50 px-4 py-2.5 text-sm text-stone-800 ring-1 ring-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-600"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setReportParticipation(null)}
                    className="flex-1 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 ring-1 ring-stone-200 transition hover:bg-stone-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingReport}
                    className="flex-1 rounded-xl bg-amber-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
                  >
                    {isSubmittingReport ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 size={16} className="animate-spin" /> Enviando...
                      </span>
                    ) : (
                      'Enviar relatorio'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Missoes;
